import type { ReactNode } from 'react'
import { BadgeDisplay } from './BadgeDisplay'
import { BADGE_TONES } from './types'
import type { BadgeWonNotificationPayload } from './badge-win'
import { twMerge } from 'tailwind-merge'

interface BadgeWinCardProps {
  action?: ReactNode
  body: string
  className?: string
  compact?: boolean
  headline: string
  payload: BadgeWonNotificationPayload
}

export function BadgeWinCard({
  action,
  body,
  className,
  compact = false,
  headline,
  payload
}: BadgeWinCardProps) {
  const tone = BADGE_TONES[payload.badgeConfig.colorId]

  return (
    <div
      data-testid='badge-win-card'
      className={twMerge(
        compact
          ? 'overflow-hidden rounded-[24px] text-white shadow-[0_18px_32px_rgba(107,114,128,0.14)]'
          : 'overflow-hidden rounded-[32px] text-white shadow-[0_24px_48px_rgba(107,114,128,0.18)]',
        className
      )}
      style={{
        background: `linear-gradient(115deg, ${tone.fillStart} 0%, ${tone.fillEnd} 100%)`
      }}>
      <div
        className={twMerge(
          'flex items-start justify-between',
          compact ? 'gap-3 px-4 pt-4 sm:px-5 sm:pt-5' : 'gap-4 px-6 pt-6'
        )}>
        <div className='min-w-0'>
          <p
            className={twMerge(
              'font-semibold uppercase tracking-[0.18em] text-white/72',
              compact ? 'text-[9px] sm:text-[10px]' : 'text-xs'
            )}>
            {payload.sourceLabel}
          </p>
          <h3
            className={twMerge(
              'mt-2 font-semibold tracking-[-0.03em] text-white',
              compact
                ? 'max-w-[12ch] text-[14px] leading-[1.08] sm:max-w-[14ch] sm:text-[16px]'
                : 'text-[36px] leading-[1.02]'
            )}>
            {headline}
          </h3>
        </div>
        {action ? <div className='shrink-0'>{action}</div> : null}
      </div>
      <div
        data-testid='badge-win-card-content'
        className={twMerge(
          'items-center gap-4',
          compact
            ? 'grid grid-cols-[68px_minmax(0,1fr)] gap-3 px-4 pb-4 pt-3 sm:grid-cols-[76px_minmax(0,1fr)] sm:gap-4 sm:px-5 sm:pb-5 sm:pt-4'
            : 'grid px-6 pb-6 pt-8 grid-cols-[184px_minmax(0,1fr)]'
        )}>
        <div className='flex justify-center'>
          <BadgeDisplay
            className='drop-shadow-[0_18px_32px_rgba(255,255,255,0.12)]'
            patternId={payload.badgeConfig.patternId ?? undefined}
            shapeId={payload.badgeConfig.shapeId}
            size={compact ? 68 : 176}
            symbol={payload.badgeConfig.symbol}
            toneId={payload.badgeConfig.colorId}
          />
        </div>
        <div className='min-w-0 text-left'>
          <p
            className={twMerge(
              'font-semibold tracking-[-0.03em] text-white',
              compact
                ? 'text-[15px] leading-[1.08] sm:text-[17px]'
                : 'text-[42px] leading-[1.02]'
            )}>
            {payload.badgeName}
          </p>
          <p
            className={twMerge(
              'text-white/90',
              compact
                ? 'mt-1.5 max-w-[11rem] text-[12px] leading-5 sm:max-w-[13rem] sm:text-[13px] sm:leading-5'
                : 'max-w-[26rem] text-[22px] leading-8'
            )}>
            {body}
          </p>
        </div>
      </div>
    </div>
  )
}
